import type { Dispatch, FC, SetStateAction } from 'react'
import { useEffect, useMemo, useState } from 'react'
import {
  CwsButton,
  CwsGrid,
  CwsGridCol,
  CwsGridRow,
  CwsH3,
  CwsIcon,
  CwsModal,
  CwsModalBody,
  CwsModalFooter,
  CwsP,
} from '@components/cws'
import { useTranslate } from '@hooks/useTranslation'
import { useRouter } from 'next/router'
import type { ReturnOrderItem } from '@components/support/Returns/OrderDetails'
import { OrderDetails } from '@components/support/Returns/OrderDetails'
import { PersonalDetails } from '@components/profile/Orders/ReturnOrder/PersonalDetails'
import type { CustomerInfo } from '@hooks/useReturnOrders'
import BankDetails from '@components/profile/Orders/ReturnOrder/BankDetails'
import axios from 'axios'
import { useGoogleRecaptcha } from '@ui/hooks/useGoogleRecaptcha'
import type { Locale } from '@model/locales'
import { KK_LANG_IDS } from '@model/locales'
import { isThereAnyRefundAndDiscountItems } from '@components/profile/Orders/ReturnOrder/utils'
// @ts-ignore
import { modal } from 'cms-web-styleguide/dist/utils/utils'
import { anotherReturnItemsSchema, returnFormPersonalInfoSchema } from '@utils/validations/returnOrder'
import { ValidationError } from 'yup'

const MODAL_ID = 'feedback-modal'

interface ReturnsProps {
  setVisible: Dispatch<SetStateAction<boolean>>
  visible: boolean
}
const Returns: FC<ReturnsProps> = ({ setVisible, visible }) => {
  const { translate } = useTranslate()
  const template = (orders: ReturnOrderItem[], personDetails: CustomerInfo) => {
    const personTemplateData = Object.entries({
      'returns.email.form.firstName': personDetails.firstName,
      'returns.email.form.lastName': personDetails.lastName,
      'returns.email.form.email': personDetails.emailAddress,
      'returns.email.form.phone': personDetails.phone,
      'returns.email.form.country': personDetails.country,
      'returns.email.form.address': personDetails.streetAddress,
      [personDetails.bankName ? 'returns.email.form.refundBank' : '']: personDetails.bankName,
      [personDetails.iban ? 'returns.email.form.refundIban' : '']: personDetails.iban,
    }).filter(([key]) => key)

    const ordersTemplateData = orders.map((order) => [
      ['return.form.order.nr', order.orderNumber],
      ['return.form.order.date', order.orderPlaced],
      ['return.form.receive.date', order.orderReceived],
      ['return.form.item.name', order.itemName],
      ['product.detail.sku', order.itemCode],
      ['return.form.item.qty', order.quantity],
      ['return.form.item.reason', order.reasonForReturn],
      ['return.form.item.action', order.actionValue],
      ['return.form.item.comment', order?.comment ?? ''],
    ])

    let personTemplate = ''
    let ordersTemplate = ''
    for (const [key, val] of personTemplateData) {
      personTemplate += `<b>${translate(key)}<b> : ${val}<br>`
    }
    for (const order of ordersTemplateData) {
      for (const [key, val] of order) {
        ordersTemplate += `<b>${translate(key)}<b> : ${translate(val)}<br>`
      }
      personTemplate += `<br><br>`
    }
    return `<h3>${translate('returns.email.form.title')}(<small>${
      personDetails.emailAddress
    }</small>) </h3><br>${personTemplate}${ordersTemplate}`
  }
  const { locale } = useRouter()
  const { executeGoogleReCaptcha } = useGoogleRecaptcha()

  const [validationErrors, setValidationErrors] = useState<string[]>([])

  const [orders, setOrders] = useState<ReturnOrderItem[]>([
    {
      orderNumber: '',
      orderPlaced: '',
      orderReceived: '',
      itemName: '',
      itemCode: '',
      quantity: '',
      reasonForReturn: '',
      actionValue: '',
      comment: '',
    },
  ])
  const [personDetails, setPersonDetails] = useState<CustomerInfo>({
    firstName: '',
    lastName: '',
    emailAddress: '',
    phone: '',
    streetAddress: '',
    postCode: '',
    country: 'Estonia',
    city: '',
  })

  useEffect(() => {
    try {
      anotherReturnItemsSchema.validateSync(orders)
      returnFormPersonalInfoSchema.validateSync(personDetails)

      setValidationErrors([])
    } catch (e) {
      if (e instanceof ValidationError) {
        setValidationErrors(e.errors)
      }
    }
  }, [personDetails, orders, setValidationErrors])

  // eslint-disable-next-line no-console
  console.log(validationErrors)

  const copyright = translate('w2.copyright.message', {
    privacyPolicy: `<a class="cws-a cws-a--theme-white cws-a--size-tiny cws-a--inline cws-a--color-dark-grey" target="_blank" href="https://policies.google.com/privacy?hl=${locale}">${translate(
      'footer.privacy.policy'
    )}</a>`,
    termsOfService: `<a class="cws-a cws-a--theme-white cws-a--size-tiny cws-a--inline cws-a--color-dark-grey" target="_blank" href="https://policies.google.com/terms?hl=${locale}">${translate(
      'w2.terms.of.service'
    )}</a>`,
  })
  const addAnotherItem = () => {
    setOrders((prevState) => {
      const currentOrders = [...prevState]
      currentOrders.push({
        orderNumber: '',
        orderPlaced: '',
        orderReceived: '',
        itemName: '',
        itemCode: '',
        quantity: '',
        reasonForReturn: '',
        actionValue: '',
        comment: '',
      })
      return currentOrders
    })
  }
  const languageId = KK_LANG_IDS[locale as Locale]
  const isBankDetails = useMemo(() => isThereAnyRefundAndDiscountItems(orders), [orders])
  const sendOrder = async () => {
    try {
      const token = await executeGoogleReCaptcha()
      await axios.post('/api/send-return-email', {
        clientEmail: personDetails.emailAddress,
        confirmationBody: translate('return.form.confirmation.email.body'),
        confirmationTitle: translate('return.form.confirmation.email.title'),
        returnFormData: template(orders, personDetails),
        languageId: languageId,
        recaptchaResponse: token,
      })
      setOrders([
        {
          orderNumber: '',
          orderPlaced: '',
          orderReceived: '',
          itemName: '',
          itemCode: '',
          quantity: '',
          reasonForReturn: '',
          actionValue: '',
          comment: '',
        },
      ])
      setPersonDetails({
        firstName: '',
        lastName: '',
        emailAddress: '',
        phone: '',
        streetAddress: '',
        postCode: '',
        country: 'Estonia',
        city: '',
      })
    } catch (e) {
      console.error(e)
    } finally {
      modal.addEventModalHide(MODAL_ID)
    }
  }
  return (
    <>
      <CwsModal
        id={MODAL_ID}
        className="overflow-auto"
        hideCloseButton
        visible={visible}
        onClose={() => setVisible(false)}
      >
        <CwsModalBody className="cws-p-0">
          <div className="cws-bg--color-seafoam">
            <CwsGrid>
              <CwsGridRow>
                <CwsGridCol col="12">
                  <div className="cws-pt-xl">
                    <div className="flex gap-3.5">
                      <CwsIcon icon="recycle" size="32px" />
                      <CwsH3>{translate('return.form.section.header.products')}</CwsH3>
                    </div>
                    <CwsP>{translate('w2.return.items.info')}</CwsP>
                  </div>
                </CwsGridCol>
              </CwsGridRow>
            </CwsGrid>
            <CwsGrid width="fluid">
              <CwsGridRow>
                <CwsGridCol col={12}>
                  <div className="flex flex-col cws-px-s">
                    <div className="flex flex-col gap-4">
                      {orders.map((order, index) => (
                        <OrderDetails key={index} order={order} setOrders={setOrders} index={index} />
                      ))}
                    </div>
                    <div className="flex justify-center cws-py-xs cursor-pointer" onClick={addAnotherItem}>
                      <CwsP color="buoy-link">+{translate('support.return.form.list.add.new')}</CwsP>
                    </div>
                  </div>
                </CwsGridCol>
                <CwsGridCol col={12}>
                  <div className="cws-bg--color-white">
                    <PersonalDetails isReturnEmail customerInfo={personDetails} setCustomerInfo={setPersonDetails} />
                  </div>
                </CwsGridCol>
                {isBankDetails && (
                  <div className="cws-bg--color-white w-full">
                    <CwsGridCol col={12}>
                      <BankDetails customerInfo={personDetails} setCustomerInfo={setPersonDetails} />
                    </CwsGridCol>
                  </div>
                )}
              </CwsGridRow>
              <div className="cws-mb-s" />
            </CwsGrid>
          </div>
        </CwsModalBody>
        <CwsModalFooter>
          <div className="w-full flex flex-col items-center gap-3.5">
            <CwsButton disabled={validationErrors.length} onClick={sendOrder}>
              {translate('return.form.submit')}
            </CwsButton>
            <CwsP color="dark-grey" dangerouslySetInnerHTML={{ __html: copyright }} size="tiny" />
          </div>
        </CwsModalFooter>
      </CwsModal>
    </>
  )
}

export default Returns
